import axios from 'axios'
import AuthService from './auth-header'
import hrEmailNotificationService, { HR_EMAIL_TEMPLATE_KEYS } from './hr-email-notification-service'
import {
  taskId as getTaskId,
  isTaskComplete,
  taskIsRecurringTemplate,
  taskRecurringTaskId,
  taskEligibleForRecurringReminder,
} from '@/utils/task-config'
import {
  buildTasksDeepLink,
  sendPushToEmpId,
} from '@/utils/fcm-push'

let API_URL = ''
    if (process.env.NODE_ENV === "production") {
        API_URL = 'https://ats.sattvahuman.in/api/'
    } else {
        API_URL = 'http://192.168.0.30:5000/api/'
    }

/**
 * ============================================================================
 * TASK / PROJECT MANAGEMENT — DEDICATED BACKEND API CONTRACT (`/api/pm/tasks`)
 * ============================================================================
 *
 * STATUS: Core `/api/pm/tasks` routes are implemented in SattvaATS
 * (`pmTaskRoutes.js`, `pmTaskController.js`) including ping, attachments,
 * history, recurrence uniqueness, and `/api/notifications`. Remaining gaps:
 * server-side escalation/recurring crons, inbox fan-out on domain events, and
 * `POST /api/pm/tasks/recurring/generate`. See backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md
 * § Status snapshot (2026-08-06).
 *
 * Namespace: chose `pm/tasks` ("Project Management") as a distinct namespace
 * from the old generic `tasks` CRUD resource (`GET/POST/PUT/DELETE tasks...`,
 * which this module used to hit and is being retired here) and from
 * `reimbursements` (a flat, single-word resource name — see
 * `reimbursement-service.js`). `tasks` alone was already taken by the legacy
 * generic resource, so a short prefix (`pm/`) keeps the new purpose-built
 * routes unambiguous without renaming/breaking the old resource for whatever
 * else might still use it.
 *
 * Auth: every request sends `AuthService.authHeader()` (`x-access-token`),
 * exactly like every other service in this codebase (see
 * `reimbursement-service.js`, `expense-service.js`, etc.). Requests with a
 * missing/expired token are expected to come back 401/403, same as today.
 *
 * Common response envelope (all endpoints): `{ status: 'OK', data, message? }`
 * on success. `status !== 'OK'` (e.g. `'ERROR'`, `'VALIDATION_ERROR'`) on
 * failure, with a human-readable `message`. This mirrors the envelope used
 * by `reimbursement-service.js` / `hr-email-notification-service.js` and is
 * what `apiError()` below normalizes any axios error into on the frontend
 * side, so callers never have to branch on "was this a network error vs a
 * validation error".
 *
 * Task row shape (request body fields AND response `data` row fields):
 *   {
 *     id:              number|string   (server-assigned, response only)
 *     title:           string          (required)
 *     description:     string          (optional, default '')
 *     status:          string          (one of `TASK_STATUSES` in
 *                                        `src/utils/task-config.js`:
 *                                        'To Do' | 'In Progress' | 'Review' |
 *                                        'Done')
 *     priority:        string          (one of `TASK_PRIORITIES`:
 *                                        'Urgent' | 'High' | 'Medium' | 'Low')
 *     assignedTo:      string          (assignee's emp_id)
 *     assignedToName:  string          (display name, sent by client so the
 *                                        backend doesn't need to join against
 *                                        the `users` table on every read;
 *                                        server MAY re-resolve/override it)
 *     dueDate:         string|null     (ISO date, 'YYYY-MM-DD')
 *     createdBy:       string          (creator's emp_id — "the assigning
 *                                        manager", or self if self-assigned)
 *     createdByName:   string          (display name, same rationale as
 *                                        `assignedToName`)
 *     order:           number          (drag-order rank within its column;
 *                                        best-effort — the frontend ALSO
 *                                        keeps its own source of truth in
 *                                        localStorage, see
 *                                        `src/utils/task-order-store.js`, so
 *                                        the backend does not need to
 *                                        guarantee this round-trips)
 *     isDeleted:       boolean         (soft-delete flag; `false` on create,
 *                                        set `true` only via the archive
 *                                        endpoint below — never via a plain
 *                                        update)
 *     createdAt / updatedAt: string    (ISO timestamps, response only,
 *                                        server-generated)
 *     attachmentName:  string|null     (optional display name of attached file)
 *     attachmentUrl:   string|null     (optional absolute/relative URL to open)
 *     file_path:       string|null     (optional — reimbursement-style path
 *                                        alias; frontend normalizes either)
 *   }
 *
 * File upload (optional supporting document on create/edit):
 *   - Multipart field name: `attachment` (`TASK_FILE_FIELD` in
 *     `src/utils/task-config.js`) — backend should use
 *     `upload.single('attachment')` (NOT multer().none()).
 *   - When a file is present (or `removeAttachment=true`), the client sends
 *     `multipart/form-data` instead of JSON; scalar task fields are
 *     string parts, arrays/objects are JSON-stringified parts.
 *   - Clearing an existing file: form field `removeAttachment` = `'true'`
 *     (no new file part). Echo cleared `attachmentName`/`attachmentUrl`/
 *     `file_path` as null in the response.
 *   - Until multer accepts the file part, the UI still saves the task as
 *     JSON and keeps a local blob preview — see `TaskBoard.vue`.
 *
 * ----------------------------------------------------------------------------
 * 1) GET /api/pm/tasks — list tasks visible to the caller
 * ----------------------------------------------------------------------------
 * Query params (all optional, sent by `getTasks()` below based on what
 * `TaskBoard.vue`/`taskTeamMixin.js` already compute client-side — the
 * backend is NOT expected to independently resolve an org chart):
 *   - empId          string   Caller's emp_id. Server should return rows
 *                              where `assignedTo === empId` OR
 *                              `createdBy === empId` ("my tasks") at minimum.
 *   - scope           string   'mine' (default) | 'team'. When 'team', ALSO
 *                              include rows belonging to the caller's direct
 *                              reports (see `teamMemberIds` below) — this is
 *                              the "My Team's Tasks" toggle.
 *   - managerId       string   Same value as `empId` when `scope=team` —
 *                              sent explicitly/redundantly so the backend can
 *                              log/validate "who is asking for whose team"
 *                              without re-deriving it from `empId`.
 *   - teamMemberIds   (omitted) Docs allow a CSV of report emp_ids, but the
 *                              live backend ignores it and derives reports
 *                              from `users.mgr_id` via `managerId`. Sending a
 *                              long CSV produced oversized URLs and 500s, so
 *                              the client no longer includes this param.
 *   Excludes rows with `isDeleted: true` by default (server-side filter;
 *   the frontend's `visibleTasks()` in `task-permissions.js` re-filters
 *   defensively regardless).
 * Response: `{ status: 'OK', data: Task[] }`
 *
 * ----------------------------------------------------------------------------
 * 2) POST /api/pm/tasks — create a task
 * ----------------------------------------------------------------------------
 * Body: full Task shape above minus `id`/timestamps (see `buildTaskPayload()`
 * in `src/utils/task-config.js` for exactly what's sent).
 * Response: `{ status: 'OK', data: Task }` (MUST include the server-assigned
 * `id` — see `extractServerId()` in `task-config.js` for how the frontend
 * reads it back out of `data` / `data[0]` / `data.id`, tolerant of a couple
 * of shapes, but `data.id` is the preferred/simplest one).
 *
 * ----------------------------------------------------------------------------
 * 3) PUT /api/pm/tasks/:id — general update
 * ----------------------------------------------------------------------------
 * Body: any subset of the Task fields the caller is allowed to change
 * (title/description/status/priority/assignedTo/dueDate/order/etc. — the
 * frontend currently always sends the full edited form, not a sparse patch).
 * Used by: the "Edit Task" modal's full-detail save path.
 * Response: `{ status: 'OK', data: Task }` (updated row).
 *
 * ----------------------------------------------------------------------------
 * 4) PATCH /api/pm/tasks/:id/status — dedicated status change
 * ----------------------------------------------------------------------------
 * Body: `{ status: string, order?: number }`
 * Used by: Kanban drag-and-drop between status columns. Kept as its OWN
 * endpoint (rather than routed through the general PUT) so the backend can
 * apply status-transition-specific validation/side-effects later (e.g.
 * "can't move back to To Do from Done", audit log of status history, etc.)
 * without that logic living inside a generic update handler.
 * Response: `{ status: 'OK', data: Task }`
 *
 * ----------------------------------------------------------------------------
 * 5) PATCH /api/pm/tasks/:id/priority — dedicated priority change
 * ----------------------------------------------------------------------------
 * Body: `{ priority: string }`
 * Used by: the priority quick-action dropdown on each card/row, AND a
 * manager's priority-override on a direct report's task (see
 * `canChangePriority()` in `task-permissions.js` — the backend should apply
 * the equivalent authorization: caller is the task's owner/creator, OR is
 * the manager of the task's current assignee).
 * Response: `{ status: 'OK', data: Task }`
 *
 * ----------------------------------------------------------------------------
 * 6) PATCH /api/pm/tasks/:id/assignee — dedicated reassignment
 * ----------------------------------------------------------------------------
 * Body: `{ assignedTo: string, assignedToName?: string }`
 * Used by: drag-and-drop between per-team-member columns in the "My Team's
 * Tasks" board. Backend should apply the equivalent of `canReassignTask()`
 * in `task-permissions.js`: caller must manage the task AND the target
 * assignee must be a valid direct report (or the caller themself).
 * Response: `{ status: 'OK', data: Task }`
 *
 * ----------------------------------------------------------------------------
 * 6b) PATCH /api/pm/tasks/:id/ping — manager ask-for-update (in-app nudge)
 * ----------------------------------------------------------------------------
 * Body: `{ lastPingedAt, pingCount, lastPingedByName?, lastPingedByEmpId?,
 *          pingSeenAt?, changeHistory? }` (ping metadata only — not a full
 * task edit). Used by: Team view per-task / per-employee "Ping for update".
 * Auth: equivalent of `canPingTask()` — caller manages a team AND the task's
 * assignee is one of their direct reports. Deliberately MUST NOT use the
 * stricter `canEditTaskDetails` / `createdBy` gate that applies to
 * `PUT /pm/tasks/:id` (full detail edit). A manager who did not create the
 * task must still be allowed to ping it.
 * Response: `{ status: 'OK', data: Task }` (updated row including ping fields
 * and any echoed `changeHistory`).
 *
 * ----------------------------------------------------------------------------
 * 7) PATCH /api/pm/tasks/:id/archive — soft delete
 * ----------------------------------------------------------------------------
 * Body: none required (server sets `isDeleted: true`, or an `{ isDeleted }`
 * body may be sent/ignored). Chosen over a plain `DELETE` for the UI's
 * "Delete" button because the old generic-CRUD contract's soft-delete
 * convention (`PUT tasks/delete/:id` -> `isDeleted: true`) is worth
 * preserving for recoverability — deleted tasks can be restored by support/
 * admin tooling later without a real DELETE having destroyed the row.
 * Response: `{ status: 'OK', data: Task }` (with `isDeleted: true`).
 *
 * ----------------------------------------------------------------------------
 * 8) DELETE /api/pm/tasks/:id — hard delete
 * ----------------------------------------------------------------------------
 * Permanently removes the row. NOT currently wired to any UI action (the
 * "Delete" button uses the archive endpoint above) — kept in the contract
 * and in this service for admin/cleanup tooling, exactly as the old
 * `deleteTask()` was in the previous generic-CRUD version of this file.
 * Response: `{ status: 'OK' }`
 *
 * ----------------------------------------------------------------------------
 * 9) GET/POST /api/pm/tasks/:id/history — task edit history (NOT YET LIVE)
 * ----------------------------------------------------------------------------
 * Confirmed 404 against the real backend as of 2026-07-21 (see
 * `historyEndpointSupported`'s doc below) — routes 1-8 above are all live
 * (auth-gated, "No token provided!"), this sub-resource specifically is
 * not. `getTaskHistory()`/`addTaskHistoryEntries()` below feature-detect
 * this at runtime and the frontend degrades gracefully (accumulates a
 * `changeHistory` array directly on the task row instead) when it's
 * missing — see the "Task Edit History" section of
 * `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for the full recommended
 * contract/schema for when the backend implements this for real.
 *
 * ============================================================================
 * MIGRATION NOTE: this replaces the previous contract, which reused the
 * generic `/api/tasks` CRUD resource (`addTask`/`getAllTasks`/`updateTask`/
 * `updateTaskDelete`/`deleteTask` hitting `POST/GET/PUT/DELETE tasks...`).
 * That generic resource has no purpose-built schema for this feature and is
 * still used elsewhere in the app for its original purpose — this module no
 * longer touches it. Old method names -> new method names, for anyone
 * grepping history: `addTask` -> `createTask`, `getAllTasks` -> `getTasks`,
 * `updateTaskDelete` -> `archiveTask`. `updateTask`/`updateTaskStatus`/
 * `updateTaskPriority`/`updateTaskAssignee`/`deleteTask`/`reorderTasks` kept
 * their names (only their underlying routes changed).
 * ============================================================================
 */

const PM_TASKS_URL = `${API_URL}pm/tasks`

/**
 * Task Edit History — feature-detected ONCE per page load rather than
 * re-probed on every single edit. `null` = not yet probed this session,
 * `true`/`false` = known from a real response. See
 * `getTaskHistory()`/`addTaskHistoryEntries()` below and the "Task Edit
 * History" section of `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for the full
 * contract. Implemented in SattvaATS — entries stored in `changeHistory` JSONB
 * on the task row (not a separate table). Kept as a runtime probe so older
 * backends without the route still degrade gracefully.
 */
let historyEndpointSupported = null

/**
 * Dedicated ping route feature flag — same probe pattern as
 * `historyEndpointSupported`. `null` = not probed yet this session.
 * When `false`, callers use local ping store only (see
 * `TaskBoard.vue#persistTaskPingToServer`) — no PUT fallback (403).
 */
let pingEndpointSupported = null

/** `true` only for a genuine "route doesn't exist" response (404/501) — as opposed to e.g. a 403/500 from a route that DOES exist but rejected/failed this particular request, which tells us nothing about whether the route itself is implemented. */
function isRouteNotImplementedError(err) {
  const statusCode = err?.response?.status
  return statusCode === 404 || statusCode === 501
}

const SERVICE_UNAVAILABLE = {
  status: 'SERVICE_UNAVAILABLE',
  message: 'Task service unavailable. Please contact admin.',
  data: [],
}

/** One soft warning per session for auth-rejected /pm/tasks (avoids poll spam). */
let taskAuthSoftWarned = false
/** One soft warning per session for server (5xx) /pm/tasks failures (avoids poll spam). */
let taskServerSoftWarned = false

/** Task ids whose general PUT was denied (403) — skip repeat network calls this session. */
const taskPutDeniedIds = new Set()

/**
 * Multer rejection copy — same idea as reimbursement-service.js. Backend
 * must accept multipart with field `attachment` (`upload.single('attachment')`).
 */
function parseMulterErrorMessage(raw) {
  if (typeof raw !== 'string' || !raw.includes('MulterError')) return null
  if (raw.includes('Unexpected field')) {
    return (
      'The server rejected the attachment. POST/PUT /pm/tasks does not accept '
      + 'file uploads yet. Save without an attachment, or ask the backend team '
      + "to enable upload.single('attachment')."
    )
  }
  if (raw.includes('LIMIT_FILE_SIZE')) {
    return 'Attachment is too large. Please choose a smaller file.'
  }
  return 'File upload failed. Try again without an attachment.'
}

/** Let axios set multipart boundary; manual Content-Type breaks FormData parsing. */
function multipartAuthHeaders() {
  const headers = AuthService.authHeaderForm()
  delete headers['Content-Type']
  return headers
}

function requestHeadersFor(data) {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return multipartAuthHeaders()
  }
  return AuthService.authHeader()
}

function isAuthHttpStatus(status) {
  return status === 401 || status === 403
}

/**
 * Soft-fail log for /pm/tasks failures. Auth (401/403) and "not implemented"
 * (404/501) are expected until the backend ships — warn once per session.
 * Other errors still warn each time (action failures need visibility).
 */
function isServerHttpStatus(status) {
  return typeof status === 'number' && status >= 500
}

function softWarnTaskFailure(label, err) {
  const httpStatus = err?.response?.status
  if (isAuthHttpStatus(httpStatus) || isRouteNotImplementedError(err)) {
    if (taskAuthSoftWarned) return
    taskAuthSoftWarned = true
    console.warn(
      `${label} (soft-fail, polling paused until next login/nav):`,
      httpStatus || err?.message,
    )
    return
  }
  if (isServerHttpStatus(httpStatus)) {
    if (taskServerSoftWarned) return
    taskServerSoftWarned = true
    const bodyMsg = err?.response?.data?.message
      || (typeof err?.response?.data === 'string' ? err.response.data : null)
    console.warn(
      `${label} (server error, background poll will back off):`,
      httpStatus || err?.message,
      bodyMsg || '',
    )
    return
  }
  console.warn(`${label}:`, httpStatus || err?.message)
}

/**
 * Defensive error unwrap — mirrors `apiError()` in reimbursement-service.js so
 * callers always get a predictable `{ status, message, httpStatus? }` shape
 * instead of a raw axios error object. Since `/pm/tasks` is brand new and
 * almost certainly 404s today (no backend route registered yet), this also
 * has to gracefully handle non-JSON 404 bodies (e.g. an HTML "Cannot GET ..."
 * page) without throwing — see the `typeof data === 'string'` branch below.
 *
 * For 401/403, `status` is the numeric HTTP code so pollers can stop retrying
 * (backend envelope `status: 'ERROR'` alone is not enough).
 */
function apiError(err, fallbackMessage = SERVICE_UNAVAILABLE.message) {
  const httpStatus = err?.response?.status
  if (isAuthHttpStatus(httpStatus)) {
    const data = err.response.data
    const message = (data && typeof data === 'object' && data.message)
      || (typeof data === 'string' ? data : null)
      || (httpStatus === 401 ? 'Unauthorized' : 'Forbidden')
    return {
      status: httpStatus,
      httpStatus,
      message,
      data: [],
    }
  }
  if (err?.response?.data) {
    const data = err.response.data
    if (typeof data === 'string') {
      const multerMessage = parseMulterErrorMessage(data)
      return {
        status: 'ERROR',
        httpStatus,
        message: multerMessage || data || fallbackMessage,
      }
    }
    const multerMessage = parseMulterErrorMessage(data.message)
    return {
      status: data.status || 'ERROR',
      httpStatus,
      message: multerMessage || data.message || fallbackMessage,
      ...data,
    }
  }
  if (err?.request) {
    // Request went out, no response came back (network/backend down, or the
    // route genuinely doesn't exist and the server dropped the connection).
    return SERVICE_UNAVAILABLE
  }
  return { status: 'ERROR', message: fallbackMessage }
}

/**
 * Task-assignment email notification — mirrors the reimbursement module's
 * pattern in `reimbursement-service.js` (`buildReimbursementEmailVariables`
 * + `sendReimbursementEmailSafe`, going through the SAME
 * `hrEmailNotificationService.sendTemplatedEmail()` -> `mailService.mailHR()`
 * pipeline), reusing the existing HR email architecture rather than
 * inventing a new one. See `HR_EMAIL_TEMPLATE_KEYS.TASK_ASSIGNED` /
 * `GENERIC_TEMPLATES` in `hr-email-notification-service.js` for the actual
 * template content, since `email-templates.js` has no task-related method.
 * Entirely independent of the `/pm/tasks` REST contract above — no route
 * change here.
 */
/**
 * Deep-link straight to a task from an email — same "hardcoded production
 * domain + hash-route + query-param id" convention already used for the
 * candidate links in `email-templates.js` (search that file for
 * `ats.sattvahuman.in/#/apps/my-candidates?...&filter=`), reused here for
 * consistency rather than inventing a new link style. Deliberately always
 * points at the PRODUCTION domain regardless of which environment
 * triggered the send — these are real notification emails a human clicks
 * from their inbox, not an in-app navigation, so `window.location.origin`
 * (which could be `localhost` in dev) would be wrong here.
 * `TaskBoard.vue#openTaskFromRouteQuery` reads the `taskId` query param
 * back out on load and opens that task's Edit modal automatically. Falls
 * back to the bare Tasks page link (no query param) when `task` has no
 * resolvable id yet (shouldn't normally happen — every caller passes an
 * already-created/loaded task — but cheap to guard).
 */
const TASKS_PAGE_URL = 'https://ats.sattvahuman.in/#/apps/tasks'
function buildTaskDeepLink(task) {
  const id = getTaskId(task)
  return id != null ? buildTasksDeepLink(id) : TASKS_PAGE_URL
}

/**
 * FCM push via POST /api/notify/push (server-side token lookup + FCM).
 * Never throws; returns `{ sent, reason?, message? }` for caller feedback.
 */
async function sendTaskPushSafe({
  empId, userId = null, title, body, taskId = null, type = 'task_notice',
}) {
  if (empId == null && userId == null) {
    return { sent: 0, reason: 'no_recipient' }
  }
  try {
    const result = await sendPushToEmpId(empId, {
      userId,
      title,
      body: body || '',
      link: buildTasksDeepLink(taskId),
      data: {
        module: 'pm_tasks',
        type: String(type || 'task_notice'),
        taskId: taskId != null ? String(taskId) : '',
      },
    })
    if (!result || !result.sent) {
      console.warn('[task-push] not delivered:', {
        empId,
        userId,
        type,
        reason: result && result.reason,
        message: result && result.message,
      })
    }
    return result || { sent: 0, reason: 'send_failed' }
  } catch (err) {
    console.warn('[task-push] send failed (non-blocking):', err)
    return { sent: 0, reason: 'error', message: err && err.message }
  }
}

function buildTaskAssignmentEmailVariables(task, extra = {}) {
  return {
    AssigneeName: extra.assigneeName || task?.assignedToName || 'there',
    TaskTitle: task?.title || '-',
    TaskDescription: task?.description || 'No description provided.',
    TaskPriority: task?.priority || '-',
    TaskDueDate: task?.dueDate || 'No due date',
    AssignedBy: extra.assignedByName || task?.createdByName || 'Your manager',
    TaskLink: buildTaskDeepLink(task),
  }
}

/**
 * Small inline HTML "what changed" table for the `TASK_UPDATED` email —
 * one row per entry in `entries` (the SAME `buildHistoryEntries()` diff
 * `TaskBoard.vue#recordTaskHistory` already computes for the in-app
 * History panel; never recomputed here, just rendered as HTML instead of
 * a `<ul>` in a modal). Deliberately plain inline-styled `<table>` markup
 * (same convention as every other body table in this file/
 * `hr-email-notification-service.js`) rather than a shared component —
 * email HTML can't use Vue/CSS classes anyway.
 */
function buildChangesSummaryHtml(entries) {
  if (!entries || !entries.length) {
    return '<p style="color:#888; margin:0;">No field-level changes were recorded for this update.</p>'
  }
  const rows = entries.map(entry => (
    '<tr>'
    + `<td style="font-weight:bold; padding:6px 10px; border-bottom:1px solid #eee; white-space:nowrap;">${entry.fieldLabel || entry.field}</td>`
    + `<td style="padding:6px 10px; border-bottom:1px solid #eee; color:#888;">${entry.oldDisplay || '—'}</td>`
    + '<td style="padding:6px 4px; border-bottom:1px solid #eee; color:#888;">&rarr;</td>'
    + `<td style="padding:6px 10px; border-bottom:1px solid #eee; font-weight:bold;">${entry.newDisplay || '—'}</td>`
    + '</tr>'
  )).join('')
  return `<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">${rows}</table>`
}

function buildTaskUpdatedEmailVariables(task, extra = {}) {
  return {
    RecipientName: extra.recipientName || 'there',
    TaskTitle: task?.title || '-',
    TaskStatus: task?.status || '-',
    TaskPriority: task?.priority || '-',
    TaskDueDate: task?.dueDate || 'No due date',
    ChangedBy: extra.changedByName || 'A team member',
    ChangesSummary: buildChangesSummaryHtml(extra.changes),
    TaskLink: buildTaskDeepLink(task),
  }
}

/**
 * Due-date escalation emails — see `getEscalationLevel()` in
 * `task-config.js` for the (pure, always-recomputed-live) level math and
 * `TaskBoard.vue#checkTaskEscalations` for who calls this and when. This
 * function itself does no date math at all; it only maps an already-
 * computed level key to its template and sends.
 */
const ESCALATION_TEMPLATE_KEY_BY_LEVEL = {
  escalation_1: HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_1,
  escalation_2: HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_2,
  final: HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_FINAL,
}

/**
 * A manager's manual "ping" (see `TaskBoard.vue#handlePingEmployee` for the
 * email/name/task-context resolution feeding this) — two distinct flavors,
 * both routed through this SAME builder; see `{{PingMessage}}`'s doc on the
 * `TASK_PING` template in `hr-email-notification-service.js`:
 *
 * - Per-EMPLOYEE (`TaskListView.vue`'s name-bell, no task-context args
 *   passed in): the original, fully generic "please go check your tasks"
 *   nudge — `{{TaskLink}}` is the plain Tasks page (`buildTaskDeepLink()`
 *   with no task -> no `?taskId=` query param), unchanged from before this
 *   per-task ping option existed.
 * - Per-TASK (`TaskListView.vue`'s per-row bell / `TaskCard.vue`'s "Ping
 *   for update" menu item, `taskTitle` present): names the specific task
 *   (+ status/due date) in the email body instead, and `{{TaskLink}}`
 *   deep-links straight to it (`taskId`, when resolvable) rather than just
 *   the Tasks page.
 */
function buildTaskPingEmailVariables({ employeeName, managerName, taskId: pingedTaskId, taskTitle, status, dueDate } = {}) {
  const pingMessage = taskTitle
    ? `is asking for an update on: <strong>${taskTitle}</strong> (Status: ${status || '-'}, Due: ${dueDate || 'No due date'}). Please review it and update your progress.`
    : 'pinged you regarding your tasks. Please review your Tasks board and update your progress.'
  return {
    EmployeeName: employeeName || 'there',
    ManagerName: managerName || 'Your manager',
    PingMessage: pingMessage,
    TaskLink: buildTaskDeepLink(pingedTaskId != null ? { id: pingedTaskId } : null),
  }
}

function buildTaskEscalationEmailVariables(task, extra = {}) {
  const days = extra.daysOverdue
  // `0` = same calendar day, past due time only — not "1 day". Body uses
  // `OverduePhrase` so we don't say "0 day(s) past its due date".
  const overduePhrase = days == null
    ? 'overdue'
    : (days === 0 ? 'past its due time (overdue today)' : `${days} day(s) past its due date`)
  return {
    AssigneeName: extra.assigneeName || task?.assignedToName || 'there',
    TaskTitle: task?.title || '-',
    TaskStatus: task?.status || '-',
    TaskPriority: task?.priority || '-',
    TaskDueDate: task?.dueDate || 'No due date',
    DaysOverdue: days != null ? String(days) : '-',
    OverduePhrase: overduePhrase,
    // Reuses the SAME `{{AssignedBy}}` placeholder name as the assignment
    // email above (not a new variable) — adds manager context/urgency to
    // the escalation body per spec, straight from the task row itself
    // (`createdByName`), no new plumbing needed from `TaskBoard.vue`.
    AssignedBy: task?.createdByName || 'their manager',
    TaskLink: buildTaskDeepLink(task),
  }
}

/**
 * Fire-and-forget send — NEVER throws/rejects, so callers (`TaskBoard.vue`)
 * can call this without awaiting it and without risking an unhandled
 * rejection blocking the create/reassign UI flow. Failures are logged only
 * (console.warn), same resilience contract as
 * `reimbursement-service.js#sendReimbursementEmailSafe`, except that one
 * re-throws for its caller to log — there is no caller-side handling needed
 * here, so this swallows outright.
 */
async function sendTaskEmailSafe(payload) {
  try {
    const result = await hrEmailNotificationService.sendTemplatedEmail(payload)
    if (result?.status !== 'OK') {
      console.warn('[task-email] send did not complete (non-blocking):', {
        to: payload.to,
        template_key: payload.template_key,
        status: result?.status,
        detail: result?.detail,
      })
    }
    return result
  } catch (err) {
    console.warn('[task-email] send threw (non-blocking):', err)
    return { status: 'FAILED' }
  }
}

/** Case-insensitive email equality — used so manager CC doesn't duplicate `to`. */
function emailsEqual(a, b) {
  if (!a || !b) return false
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * Manager CC for task emails — returns the manager address only when it is
 * present AND not already the `to` recipient (no duplicate To/CC). Missing
 * manager email → `''` (omit CC; never throw). Callers resolve the address
 * from `task.createdBy` via `TaskBoard.vue#resolveManagerEmail` and pass it
 * as `assignedByEmail` / `cc`.
 */
function buildManagerCc(toEmail, managerEmail) {
  if (!managerEmail || !String(managerEmail).trim()) return ''
  if (emailsEqual(toEmail, managerEmail)) return ''
  return String(managerEmail).trim()
}

function buildTaskListQuery({ scope = 'mine', empId = null, managerId = null } = {}) {
  const params = new URLSearchParams()
  params.set('scope', scope)
  if (empId != null) params.set('empId', String(empId))
  if (scope === 'team' && managerId != null) {
    // Backend resolves the team from managerId (`users.mgr_id`). Do not send
    // teamMemberIds — long CSVs inflate the URL and have caused 500s while
    // being ignored server-side (see TASK_MANAGEMENT_* docs).
    params.set('managerId', String(managerId))
  }
  return params.toString()
}

class TaskService {
  /**
   * `createTask` — POST /pm/tasks (was `addTask` against the old generic
   * `tasks` resource).
   */
  async createTask(data) {
    try {
      const res = await axios.post(PM_TASKS_URL, data, {
        headers: requestHeadersFor(data),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] createTask failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to create task.')
    }
  }

  /**
   * `getTasks` — GET /pm/tasks (was `getAllTasks` against the old generic
   * `tasks` resource, which returned the entire unscoped table). See the
   * contract doc above for the query params; callers pass whatever
   * `TaskBoard.vue` already knows about the current user/team so the
   * backend doesn't need its own org-chart lookup.
   */
  async getTasks(options = {}) {
    try {
      const query = buildTaskListQuery(options)
      const res = await axios.get(`${PM_TASKS_URL}?${query}`, {
        headers: AuthService.authHeader(),
        // Resolve non-2xx so callers get a normalized envelope instead of a
        // rejected promise (and the browser console stays quiet on poll 5xx).
        validateStatus: () => true,
      })
      if (res.status >= 200 && res.status < 300) {
        return res.data
      }
      const err = { response: res }
      softWarnTaskFailure('[task] getTasks failed', err)
      const fallback = res.status >= 500
        ? (res.data?.message || `Task list failed (${res.status}). Please try again.`)
        : SERVICE_UNAVAILABLE.message
      return apiError(err, fallback)
    } catch (err) {
      softWarnTaskFailure('[task] getTasks failed', err)
      return apiError(err, SERVICE_UNAVAILABLE.message)
    }
  }

  /** General update — PUT /pm/tasks/:id. Used by the "Edit Task" modal's full-detail save path. Accepts JSON or FormData (when attaching/removing a file). */
  async updateTask(id, data) {
    const idKey = id != null ? String(id) : ''
    if (idKey && taskPutDeniedIds.has(idKey)) {
      return {
        status: 403,
        httpStatus: 403,
        message: 'Task update not permitted.',
        data: null,
      }
    }
    try {
      const res = await axios.put(`${PM_TASKS_URL}/${id}`, data, {
        headers: requestHeadersFor(data),
      })
      return res.data
    } catch (err) {
      if (err?.response?.status === 403 && idKey) {
        taskPutDeniedIds.add(idKey)
      }
      softWarnTaskFailure('[task] updateTask failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to update task.')
    }
  }

  /**
   * Update ONLY the status of a task (drag between Kanban columns).
   * PATCH /pm/tasks/:id/status — dedicated route (see contract doc above
   * for why this stayed a separate endpoint instead of the general PUT).
   */
  async updateTaskStatus(id, status, extra = {}) {
    try {
      const res = await axios.patch(`${PM_TASKS_URL}/${id}/status`, { status, ...extra }, {
        headers: AuthService.authHeader(),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] updateTaskStatus failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to update task status.')
    }
  }

  /**
   * Update ONLY the priority of a task. PATCH /pm/tasks/:id/priority. Used
   * by the manager priority-override quick action and the inline priority
   * dropdown.
   */
  async updateTaskPriority(id, priority, extra = {}) {
    try {
      const res = await axios.patch(`${PM_TASKS_URL}/${id}/priority`, { priority, ...extra }, {
        headers: AuthService.authHeader(),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] updateTaskPriority failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to update task priority.')
    }
  }

  /**
   * Update ONLY the assignee of a task (drag between per-member columns in
   * the "My Team's Tasks" Kanban view). PATCH /pm/tasks/:id/assignee.
   */
  async updateTaskAssignee(id, assignedTo, extra = {}) {
    try {
      const res = await axios.patch(`${PM_TASKS_URL}/${id}/assignee`, { assignedTo, ...extra }, {
        headers: AuthService.authHeader(),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] updateTaskAssignee failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to reassign task.')
    }
  }

  /**
   * Manager ask-for-update — PATCH /pm/tasks/:id/ping.
   *
   * Auth must follow `canPingTask` (manager of the assignee), NOT
   * `canEditTaskDetails` / `createdBy`. Returns
   * `{ status: 'NOT_IMPLEMENTED' }` on 404/501 so callers can fall back
   * without treating "route missing" as a hard failure. Once probed
   * unsupported, skips the network call for the rest of the session.
   */
  async pingTask(id, data = {}) {
    if (pingEndpointSupported === false) {
      return { status: 'NOT_IMPLEMENTED', message: 'Ping endpoint not available.', data: null }
    }
    try {
      const res = await axios.patch(`${PM_TASKS_URL}/${id}/ping`, data, {
        headers: AuthService.authHeader(),
      })
      pingEndpointSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        pingEndpointSupported = false
        return { status: 'NOT_IMPLEMENTED', message: 'Ping endpoint not available.', data: null }
      }
      softWarnTaskFailure('[task] pingTask failed', err)
      return apiError(err, 'Failed to save ping.')
    }
  }

  /**
   * Soft delete — PATCH /pm/tasks/:id/archive (was `updateTaskDelete`
   * against the old `PUT tasks/delete/:id` route). See contract doc above
   * for why archive (recoverable) was chosen over hard delete for the UI's
   * "Delete" button.
   */
  async archiveTask(id) {
    try {
      const res = await axios.patch(`${PM_TASKS_URL}/${id}/archive`, { isDeleted: true }, {
        headers: AuthService.authHeader(),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] archiveTask failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to delete task.')
    }
  }

  /**
   * Hard delete — DELETE /pm/tasks/:id. Not currently wired to any UI
   * action (see contract doc above) — reserved for admin/cleanup tooling.
   */
  async deleteTask(id) {
    try {
      const res = await axios.delete(`${PM_TASKS_URL}/${id}`, {
        headers: AuthService.authHeader(),
      })
      return res.data
    } catch (err) {
      softWarnTaskFailure('[task] deleteTask failed (pm/tasks not implemented yet?)', err)
      return apiError(err, 'Failed to delete task.')
    }
  }

  /**
   * `GET /pm/tasks/:id/history` — fetch the full edit-history list for a
   * task from the dedicated backend endpoint, IF it exists (see the
   * `historyEndpointSupported` doc above). Returns
   * `{ status: 'NOT_IMPLEMENTED', data: [] }` — deliberately NOT a generic
   * `'ERROR'` — specifically when the route 404s/501s, so callers
   * (`TaskFormModal.vue`'s History panel) can tell "backend doesn't have
   * this yet, fall back to `task.changeHistory`" apart from a real failure
   * worth surfacing to the user. Skips the network call entirely once
   * `historyEndpointSupported === false` (no point re-probing a
   * known-404 route on every single history-panel open for the rest of
   * the session).
   */
  async getTaskHistory(id) {
    if (historyEndpointSupported === false) {
      return { status: 'NOT_IMPLEMENTED', data: [] }
    }
    try {
      const res = await axios.get(`${PM_TASKS_URL}/${id}/history`, {
        headers: AuthService.authHeader(),
      })
      historyEndpointSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        historyEndpointSupported = false
        return { status: 'NOT_IMPLEMENTED', data: [] }
      }
      softWarnTaskFailure('[task] getTaskHistory failed', err)
      return apiError(err, 'Failed to load task history.')
    }
  }

  /**
   * `POST /pm/tasks/:id/history` — append one or more change-history
   * entries via the dedicated endpoint, if supported. `entries` is the
   * array built by `buildHistoryEntries()` in `task-config.js` (shape:
   * `{ field, fieldLabel, oldValue, newValue, oldDisplay, newDisplay,
   * changedBy, changedByName, changedAt }`). Same `NOT_IMPLEMENTED`
   * contract as `getTaskHistory()` above — callers
   * (`TaskBoard.vue#recordTaskHistory`) fall back to embedding
   * `changeHistory` directly on the task row via `updateTask()` when this
   * comes back `NOT_IMPLEMENTED`.
   */
  async addTaskHistoryEntries(id, entries) {
    if (!entries || !entries.length) return { status: 'OK', data: [] }
    if (historyEndpointSupported === false) {
      return { status: 'NOT_IMPLEMENTED', data: [] }
    }
    try {
      const res = await axios.post(`${PM_TASKS_URL}/${id}/history`, { entries }, {
        headers: AuthService.authHeader(),
      })
      historyEndpointSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        historyEndpointSupported = false
        return { status: 'NOT_IMPLEMENTED', data: [] }
      }
      softWarnTaskFailure('[task] addTaskHistoryEntries failed', err)
      return apiError(err, 'Failed to record task history.')
    }
  }

  /**
   * Best-effort bulk reorder. There is no bulk endpoint in the new contract
   * either (ordering is a client-side UX concern — see
   * `src/utils/task-order-store.js`), so this fires individual `updateTask`
   * PUT calls in parallel and reports partial failure — callers should
   * treat the `order` field as "nice to have" server-side and rely on
   * localStorage as the source of truth for column ordering regardless of
   * whether these calls succeed.
   */
  async reorderTasks(orderedIdOrderPairs) {
    const results = await Promise.allSettled(
      (orderedIdOrderPairs || []).map(({ id, order }) => this.updateTask(id, { order })),
    )
    const failures = results
      .map((result, index) => ({ result, pair: orderedIdOrderPairs[index] }))
      .filter(({ result }) => result.status === 'rejected' || result.value?.status === 'ERROR')
    return { status: failures.length ? 'PARTIAL' : 'OK', failures }
  }

  /**
   * Notify a team member by email that a task has just been assigned to
   * them — called from `TaskBoard.vue` after (a) creating a task with an
   * assignee, (b) editing a task's assignee, and (c) drag-and-drop
   * reassignment between per-member Kanban columns. Deliberately
   * fire-and-forget (not awaited by callers) and never throws — see
   * `sendTaskEmailSafe` above. Entirely independent of the `/pm/tasks` REST
   * contract — no route change here.
   *
   * `assigneeEmail` comes from the SAME team-member data source already
   * used for task permissions (`taskTeamMixin.js#taskTeamMemberEmail`,
   * itself backed by `userService.getAllTeamMembersList`) — no new backend
   * support was added/needed for this. When it's unavailable (field
   * missing from that API response, or an unknown assignee), the send is
   * skipped with a console warning rather than failing loudly.
   *
   * `assignedByEmail` (optional): cc's the assigning manager, resolved by
   * the caller via `TaskBoard.vue#resolveManagerEmail` — same helper/
   * contract `notifyTaskEscalation` below already uses. NEDVER an HR
   * address (see the CC bug fix in `hr-email-notification-service.js` —
   * `sendTemplatedEmail()` used to silently merge in an HR cc on every
   * templated send; this module's calls to it are unaffected by that now
   * that the merge is scoped to reimbursement templates only).
   */
  notifyTaskAssigned({
    task, assigneeEmail, assigneeName, assignedByName, assignedByEmail = null,
    assigneeEmpId = null, assigneeUserId = null,
  }) {
    const taskTitle = (task && task.title) || 'a task'
    sendTaskPushSafe({
      empId: assigneeEmpId != null ? assigneeEmpId : (task && task.assignedTo),
      userId: assigneeUserId,
      title: 'New task assigned',
      body: `${assignedByName || 'Your manager'} assigned you: ${taskTitle}`,
      taskId: getTaskId(task),
      type: 'task_assigned',
    })
    if (!assigneeEmail) {
      console.warn('[task-email] skipped — no email on file for assignee', task && (task.assignedTo ?? task.assignedToName))
      return
    }
    // Field names: `to` = assignee (`assignedTo` → email); `cc` = manager
    // (`task.createdBy` → `assignedByEmail`). Skip CC if missing / same as To.
    sendTaskEmailSafe({
      template_key: HR_EMAIL_TEMPLATE_KEYS.TASK_ASSIGNED,
      to: assigneeEmail,
      cc: buildManagerCc(assigneeEmail, assignedByEmail),
      variables: buildTaskAssignmentEmailVariables(task, { assigneeName, assignedByName }),
    })
  }

  /**
   * Push-only notify when an EXISTING task changes (status/priority/due
   * date/reassignment/etc.) — FCM `task_updated` to the task manager
   * (project PM or `createdBy`, resolved in `TaskBoard.vue`). Email is
   * NOT sent here; assignment and completion use their own templates.
   * Called from `TaskBoard.vue#recordTaskHistory` with the SAME
   * `buildHistoryEntries()` diff used for the History panel. Completion
   * (`status → Done`) is handled by `notifyTaskCompleted` instead.
   */
  notifyTaskUpdated({
    task, changedByName,
    managerEmpId = null, managerUserId = null,
  }) {
    const taskTitle = (task && task.title) || 'a task'
    if (managerEmpId == null && managerUserId == null) {
      console.warn('[task-push] update notice skipped — no manager recipient', task && getTaskId(task))
      return
    }
    sendTaskPushSafe({
      empId: managerEmpId,
      userId: managerUserId,
      title: 'Task updated',
      body: `${changedByName || 'Someone'} updated: ${taskTitle}`,
      taskId: getTaskId(task),
      type: 'task_updated',
    })
  }

  /**
   * Task marked Done — email (+ optional FCM push) to relevant parties
   * (creator, project PM, assignee when distinct from the completer).
   * Recipients are pre-resolved in `TaskBoard.vue#notifyTaskCompleted`.
   * Uses `TASK_UPDATED` template with a completion-focused status row.
   */
  notifyTaskCompleted({
    task, completedByName, assigneeName = null, recipients = [],
  }) {
    if (!task) return
    const taskTitle = task.title || 'a task'
    const who = completedByName || assigneeName || 'A team member'
    const tid = getTaskId(task)
    const completionChanges = [{
      field: 'status',
      fieldLabel: 'Status',
      oldDisplay: 'In progress',
      newDisplay: 'Done',
    }]
    const seenEmpIds = new Set()
    ;(recipients || []).forEach((recipient) => {
      const empKey = recipient?.empId != null ? String(recipient.empId) : null
      if (empKey && seenEmpIds.has(empKey)) return
      if (empKey) seenEmpIds.add(empKey)

      if (recipient.empId != null || recipient.userId != null) {
        sendTaskPushSafe({
          empId: recipient.empId,
          userId: recipient.userId,
          title: 'Task completed',
          body: `${who} completed: ${taskTitle}`,
          taskId: tid,
          type: 'task_completed',
        })
      }
      if (recipient.email) {
        sendTaskEmailSafe({
          template_key: HR_EMAIL_TEMPLATE_KEYS.TASK_UPDATED,
          to: recipient.email,
          cc: '',
          variables: buildTaskUpdatedEmailVariables(task, {
            changedByName: who,
            recipientName: recipient.name || 'there',
            changes: completionChanges,
          }),
        })
      }
    })
    if (!seenEmpIds.size && !(recipients || []).some(r => r?.email)) {
      console.warn('[task-email] completion notice skipped — no recipients resolved', tid)
    }
  }

  /**
   * Fire an escalation email for an overdue task at the given level
   * (`'escalation_1'` / `'escalation_2'` / `'final'`, as returned by
   * `getEscalationLevel()` in `task-config.js`). Called from
   * `TaskBoard.vue#checkTaskEscalations` — a client-driven, best-effort
   * check (there is no backend cron for the `/pm/tasks` resource, unlike
   * the invoice module's server-side `notificationSchedules`; see that
   * method's own doc comment for the full reasoning). Same fire-and-forget,
   * never-throws contract as `notifyTaskAssigned` above — this does no
   * date/level math itself, it only maps the already-computed level to a
   * template and sends.
   *
   * `assignedByEmail` (optional): when provided, cc's the assigning
   * manager on ALL THREE escalation tiers — not just `'final'` — so a
   * manager gets visibility into an overdue task as soon as it starts
   * escalating, not only once it's reached the most urgent tier. This was
   * previously final-only; broadened per product request ("employee's
   * manager should be mentioned in their escalation mail whenever a task
   * is incomplete/overdue", not just at the last tier). Kept as a CC
   * (visible to the employee, same as the original final-tier behavior)
   * rather than switched to BCC — consistency with the already-established
   * field/visibility choice mattered more here than inventing a
   * progressively-quieter-then-louder scheme, and an employee seeing their
   * manager was copied is arguably a feature (transparency), not a bug.
   * See `TaskBoard.vue#resolveManagerEmail` for how that address gets
   * resolved (best-effort; may legitimately be `null`, in which case this
   * simply omits the cc rather than failing the send).
   */
  notifyTaskEscalation({
    task, level, assigneeEmail, assigneeName, daysOverdue, assignedByEmail = null,
    assigneeEmpId = null, assigneeUserId = null,
    managerEmpId = null, managerUserId = null,
  }) {
    // Never mail/push for completed, deleted, or recurring-template rows —
    // mirrors `getEscalationLevel()` so a caller that bypasses the board
    // check still cannot escalate a Done/Completed task.
    if (!task || task.isDeleted) {
      console.warn('[task-email] escalation skipped — missing/deleted task')
      return
    }
    if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) {
      console.warn('[task-email] escalation skipped — recurring template is not a work item')
      return
    }
    if (taskRecurringTaskId(task) != null) {
      console.warn('[task-email] escalation skipped — recurring occurrence uses reminder only')
      return
    }
    if (isTaskComplete(task)) {
      console.warn('[task-email] escalation skipped — task is Done/Completed', getTaskId(task))
      return
    }

    const templateKey = ESCALATION_TEMPLATE_KEY_BY_LEVEL[level]
    const taskTitle = (task && task.title) || 'a task'
    // Same calendar-day wording as email `OverduePhrase` — never inflate
    // same-day time-overdue into "1 day(s) overdue".
    const daysLabel = daysOverdue == null
      ? 'overdue'
      : (daysOverdue === 0 ? 'overdue today' : `${daysOverdue} day(s) overdue`)
    const levelLabel = level === 'final' ? 'FINAL escalation' : (level || 'escalation').replace(/_/g, ' ')
    const pushBody = `${taskTitle} — ${daysLabel} (${levelLabel})`
    const tid = getTaskId(task)

    // Push to assignee (always attempt) + assigning manager when distinct.
    sendTaskPushSafe({
      empId: assigneeEmpId != null ? assigneeEmpId : (task && task.assignedTo),
      userId: assigneeUserId,
      title: 'Task escalation',
      body: pushBody,
      taskId: tid,
      type: 'task_escalation',
    })
    if (managerEmpId != null && String(managerEmpId) !== String(assigneeEmpId != null ? assigneeEmpId : task && task.assignedTo)) {
      sendTaskPushSafe({
        empId: managerEmpId,
        userId: managerUserId,
        title: 'Team task escalation',
        body: pushBody,
        taskId: tid,
        type: 'task_escalation',
      })
    }

    if (!templateKey) {
      console.warn('[task-email] skipped — unknown escalation level', level)
      return
    }
    if (!assigneeEmail) {
      console.warn('[task-email] skipped — no email on file for assignee', task && (task.assignedTo ?? task.assignedToName))
      return
    }
    // Field names: `to` = assignee (`assignedTo`); `cc` = manager
    // (`task.createdBy` → `assignedByEmail`) on every escalation tier.
    // Missing manager email → omit CC (no crash).
    sendTaskEmailSafe({
      template_key: templateKey,
      to: assigneeEmail,
      cc: buildManagerCc(assigneeEmail, assignedByEmail),
      variables: buildTaskEscalationEmailVariables(task, { assigneeName, daysOverdue }),
    })
  }

  /**
   * Gentle due-date reminder for recurring-task OCCURRENCES only — replaces
   * the overdue escalation ladder (`notifyTaskEscalation`) for those rows.
   * Called from `TaskBoard.vue#checkTaskReminders` (client-driven, same
   * reliability model as escalations). Backend cron, if added later, should
   * mirror this split: recurring occurrences → reminder; normal tasks → escalation.
   */
  notifyTaskReminder({
    task, assigneeEmail, assigneeName, assignedByEmail = null,
    assigneeEmpId = null, assigneeUserId = null,
    managerEmpId = null, managerUserId = null,
  }) {
    if (!task || task.isDeleted) return
    if (taskRecurringTaskId(task) == null) return
    if (isTaskComplete(task)) return
    if (!taskEligibleForRecurringReminder(task)) return

    const taskTitle = (task && task.title) || 'a task'
    const tid = getTaskId(task)
    const pushBody = `${taskTitle} — friendly reminder (recurring task due)`

    sendTaskPushSafe({
      empId: assigneeEmpId != null ? assigneeEmpId : (task && task.assignedTo),
      userId: assigneeUserId,
      title: 'Task reminder',
      body: pushBody,
      taskId: tid,
      type: 'task_reminder',
    })
    if (managerEmpId != null && String(managerEmpId) !== String(assigneeEmpId != null ? assigneeEmpId : task && task.assignedTo)) {
      sendTaskPushSafe({
        empId: managerEmpId,
        userId: managerUserId,
        title: 'Team task reminder',
        body: pushBody,
        taskId: tid,
        type: 'task_reminder',
      })
    }

    if (!assigneeEmail) {
      console.warn('[task-email] reminder skipped — no email on file for assignee', task && (task.assignedTo ?? task.assignedToName))
      return
    }
    sendTaskEmailSafe({
      template_key: HR_EMAIL_TEMPLATE_KEYS.TASK_REMINDER,
      to: assigneeEmail,
      cc: buildManagerCc(assigneeEmail, assignedByEmail),
      variables: buildTaskEscalationEmailVariables(task, { assigneeName, daysOverdue: null }),
    })
  }

  /**
   * Manager ping — push is ON by default; email remains OFF unless
   * `sendEmail: true`.
   *
   * Cross-user delivery also persists via `PATCH /pm/tasks/:id/ping` so the
   * assignee's poller can toast + bell when FCM is unavailable — unless
   * `VUE_APP_FCM_PUSH_ONLY=true` (default), in which case only FCM/OS push
   * is used for task pings.
   *
   * `taskId`/`taskTitle`/`status`/`dueDate` are optional and only ever
   * passed together (per-task) or all omitted (per-employee) — see
   * `buildTaskPingEmailVariables()` for how that changes the body/link.
   */
  async notifyTaskPing({
    employeeEmail, employeeName, managerName, taskId, taskTitle, status, dueDate,
    sendEmail = false, employeeEmpId = null, employeeUserId = null, managerEmpId = null,
  }) {
    if (
      employeeEmpId != null
      && managerEmpId != null
      && String(employeeEmpId) === String(managerEmpId)
    ) {
      return { sent: 0, reason: 'self_ping' }
    }
    const who = managerName || 'Your manager'
    const pushTitle = 'Task ping'
    const pushBody = taskTitle
      ? `${who} asked for an update on "${taskTitle}"`
      : `${who} asked for an update on your tasks`
    const pushResult = await sendTaskPushSafe({
      empId: employeeEmpId,
      userId: employeeUserId,
      title: pushTitle,
      body: pushBody,
      taskId: taskId != null ? taskId : null,
      type: 'task_ping',
    })

    if (!sendEmail) return pushResult
    if (!employeeEmail) {
      console.warn('[task-email] ping skipped — no email on file for employee', employeeName)
      return pushResult
    }
    sendTaskEmailSafe({
      template_key: HR_EMAIL_TEMPLATE_KEYS.TASK_PING,
      to: employeeEmail,
      cc: '',
      variables: buildTaskPingEmailVariables({ employeeName, managerName, taskId, taskTitle, status, dueDate }),
    })
    return pushResult
  }
}

export default new TaskService()
export { apiError, SERVICE_UNAVAILABLE, PM_TASKS_URL, parseMulterErrorMessage }
